import { useState, useEffect } from "react";
import { Flame, TrendingUp, TrendingDown, Clock, CheckCircle2, Crosshair, X, Target } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { usePumpPortalTrades } from "@/hooks/usePumpPortalTrades";
import { useSocket } from "@/context/SocketContext";
import { useSvixEngine } from "@/hooks/useSvixEngine";
import { useLivePrices } from "@/hooks/useLivePrices";
import { fetchTokenMeta } from "@/lib/tokenMeta";

interface SvixPosition {
  id: string;
  side: "up" | "down";
  wagerSol: number;
  entryMcUsd: number;
  entryTs: number;
  status: "open" | "closed";
  leverage: number;
  exitMcUsd?: number;
  pnlSol?: number;
  
  // Token Metadata
  mint: string;
  tokenName: string;
  tokenSymbol: string;
  tokenImageUri?: string;
  
  // Legacy support for older positions
  entrySvix?: number;
  exitSvix?: number;
}

function truncate(str: string, len = 6) {
  if (!str) return "";
  if (str.length <= len * 2) return str;
  return str.slice(0, len) + "..." + str.slice(-len);
}

function formatDuration(ms: number) {
  if (!ms || ms < 0) return "0s";
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const remainingSecs = secs % 60;
  if (mins < 60) return `${mins}m ${remainingSecs}s`;
  const hrs = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  if (hrs < 24) return `${hrs}h ${remainingMins}m`;
  return `${hrs}h+`;
}

function formatUsdCompact(val: number) {
  if (val >= 1e9) return "$" + (val / 1e9).toFixed(2) + "B";
  if (val >= 1e6) return "$" + (val / 1e6).toFixed(2) + "M";
  if (val >= 1e3) return "$" + (val / 1e3).toFixed(2) + "K";
  return "$" + val.toFixed(2);
}

export function SvixTradingPanel() {
  const { caMintInput, addAppToast } = useApp() as any;
  const { rows: liveRows } = usePumpPortalTrades(caMintInput, 200);
  const svix = useSvixEngine(liveRows);
  const { serverOpenPositions } = useSocket();
  
  const [wager, setWager] = useState(0.5);
  const [leverage, setLeverage] = useState(1);
  const [positions, setPositions] = useState<SvixPosition[]>([]);
  const [paperBalance, setPaperBalance] = useState(10); // starting paper SOL
  
  const openMints = Array.from(new Set(positions.filter(p => p.status === "open").map(p => p.mint)));
  const livePrices = useLivePrices(openMints);

  // Load positions from local storage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(`svix_positions`);
      if (saved) setPositions(JSON.parse(saved));
      const bal = localStorage.getItem(`svix_balance`);
      if (bal) setPaperBalance(Number(bal));
    } catch (e) {}
  }, []);

  // Save to local storage on change
  useEffect(() => {
    localStorage.setItem(`svix_positions`, JSON.stringify(positions));
    localStorage.setItem(`svix_balance`, String(paperBalance));
  }, [positions, paperBalance]);

  const currentSvixValue = svix?.svix || 20;

  const handleOpenPosition = async (side: "up" | "down", mintOverride?: string) => {
    if (paperBalance < wager) return;
    const mintToUse = mintOverride || caMintInput;
    if (!mintToUse) {
      addAppToast("Please select a token first.", "error");
      return;
    }
    
    const meta = await fetchTokenMeta(mintToUse);
    if (!meta) {
      addAppToast("Could not fetch token metadata.", "error");
      return;
    }
    
    const currentLiveMc = (mintToUse === caMintInput && liveRows[0]?.mcUsd) 
      ? liveRows[0].mcUsd 
      : (meta.pumpMcUsd || meta.priceUsd);
    
    if (!currentLiveMc) {
      addAppToast("Could not determine current token price. Please wait for a trade.", "error");
      return;
    }
    
    setPaperBalance(prev => prev - wager);
    
    const newPos: SvixPosition = {
      id: `svix-${Date.now()}`,
      side,
      wagerSol: wager,
      entryMcUsd: currentLiveMc,
      entryTs: Date.now(),
      status: "open",
      leverage,
      mint: mintToUse,
      tokenName: meta.name || truncate(mintToUse),
      tokenSymbol: meta.symbol || "UNKNOWN",
      tokenImageUri: meta.imageUri || undefined
    };
    
    setPositions(prev => [newPos, ...prev]);
  };

  const handleClosePosition = (id: string) => {
    setPositions(prev => prev.map(p => {
      if (p.id !== id || p.status === "closed") return p;
      
      const isLegacy = typeof p.entryMcUsd !== "number";
      let rawPnl = 0;
      let exitMcUsd = 0;
      
      if (isLegacy) {
        // Legacy SVIX Index PnL calculation
        const svixDiff = p.side === "up" 
          ? (currentSvixValue - (p.entrySvix || 20))
          : ((p.entrySvix || 20) - currentSvixValue);
        const pnlPct = svixDiff / 100;
        rawPnl = p.wagerSol * (p.leverage || 1) * pnlPct;
      } else {
        // Token Market Cap PnL calculation
        exitMcUsd = livePrices[p.mint] || p.entryMcUsd;
        const mcDiffPct = p.side === "up" 
          ? ((exitMcUsd - p.entryMcUsd) / p.entryMcUsd)
          : ((p.entryMcUsd - exitMcUsd) / p.entryMcUsd);
        rawPnl = p.wagerSol * (p.leverage || 1) * mcDiffPct;
      }
      
      // Add back the original wager plus PnL
      setPaperBalance(bal => bal + p.wagerSol + rawPnl);
      
      return {
        ...p,
        status: "closed",
        exitMcUsd: isLegacy ? undefined : exitMcUsd,
        exitSvix: isLegacy ? currentSvixValue : undefined,
        pnlSol: rawPnl
      };
    }));
  };

  const handleCloseAll = () => {
    const openPos = positions.filter(p => p.status === "open");
    if (openPos.length === 0) return;
    if (!window.confirm("Close ALL open SVIX positions at market price?")) return;
    openPos.forEach(p => handleClosePosition(p.id));
  };

  return (
    <div className="flex flex-col h-full bg-[#0b0e11] text-white p-4 overflow-y-auto custom-scrollbar">
      
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-black flex items-center gap-2">
            <Flame className="text-red-500" />
            SVIX Markets
          </h2>
          <div className="text-xs text-white/50 mt-1">Leveraged Token Futures (Paper)</div>
        </div>
        <div className="flex flex-col items-end">
          <div className="text-xs text-white/50 uppercase tracking-widest font-bold">Paper Balance</div>
          <div className="text-lg font-mono text-emerald-400">{paperBalance.toFixed(2)} SOL</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 flex flex-col items-center justify-center relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t from-red-500/10 to-transparent opacity-50" />
          <div className="text-[10px] font-black uppercase tracking-widest text-white/50 mb-1 z-10">SVIX Volatility Index</div>
          <div className="text-4xl font-mono font-black text-white z-10">
            {currentSvixValue.toFixed(1)}
          </div>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 flex flex-col justify-center gap-2">
          <div className="flex justify-between items-center text-xs">
            <span className="text-white/50 uppercase tracking-widest font-bold text-[9px]">Toxicity</span>
            <span className="font-mono text-red-400">{((svix?.toxicity || 0) * 100).toFixed(0)}%</span>
          </div>
          <div className="w-full bg-black/50 rounded-full h-1.5 border border-white/5">
            <div className="bg-red-500 h-full rounded-full transition-all duration-300" style={{ width: `${(svix?.toxicity || 0) * 100}%` }} />
          </div>
          
          <div className="flex justify-between items-center text-xs mt-2">
            <span className="text-white/50 uppercase tracking-widest font-bold text-[9px]">Crowding</span>
            <span className="font-mono text-blue-400">{((svix?.retailCrowding || 0) * 100).toFixed(0)}%</span>
          </div>
          <div className="w-full bg-black/50 rounded-full h-1.5 border border-white/5">
            <div className="bg-blue-500 h-full rounded-full transition-all duration-300" style={{ width: `${(svix?.retailCrowding || 0) * 100}%` }} />
          </div>
        </div>
      </div>

      <div className="bg-[#121212] border border-white/10 rounded-xl p-4 mb-6">
        <div className="text-[10px] font-black uppercase tracking-widest text-white/50 mb-4 flex items-center gap-2">
          <Crosshair className="size-3" /> Place Leveraged Wager
        </div>
        
        <div className="flex gap-4 mb-4">
          <div className="flex-1">
            <label className="text-[9px] uppercase tracking-widest font-bold text-white/40 mb-1 block">Wager (SOL)</label>
            <input 
              type="number" 
              min="0.1" 
              step="0.1" 
              value={wager} 
              onChange={(e) => setWager(Number(e.target.value) || 0.1)}
              className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono focus:border-red-500/50 outline-none transition-colors"
            />
          </div>
          <div className="flex-1">
            <label className="text-[9px] uppercase tracking-widest font-bold text-white/40 mb-1 block">Leverage</label>
            <select 
              value={leverage}
              onChange={(e) => setLeverage(Number(e.target.value))}
              className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono focus:border-red-500/50 outline-none transition-colors"
            >
              <option value="1">1x</option>
              <option value="5">5x</option>
              <option value="10">10x</option>
              <option value="50">50x</option>
            </select>
          </div>
        </div>

        <div className="flex gap-3">
          <button 
            onClick={() => handleOpenPosition("up")}
            disabled={paperBalance < wager}
            className="flex-1 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 py-3 rounded-xl font-black uppercase tracking-widest text-xs flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <TrendingUp className="size-4" /> LONG (UP)
          </button>
          <button 
            onClick={() => handleOpenPosition("down")}
            disabled={paperBalance < wager}
            className="flex-1 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 py-3 rounded-xl font-black uppercase tracking-widest text-xs flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <TrendingDown className="size-4" /> SHORT (DOWN)
          </button>
        </div>
      </div>

      {/* ── Fade The Bot (PvE) ── */}
      <div className="bg-[#121212] border border-white/10 rounded-xl p-4 mb-6">
        <div className="text-[10px] font-black uppercase tracking-widest text-white/50 mb-4 flex items-center gap-2">
          <Target className="size-3" /> Fade The Bot (PvE)
        </div>
        <div className="text-[9px] text-white/40 mb-3 font-mono leading-relaxed">
          Short the Server's live positions. If the ALgoat hits its Stop Loss, you win. If ALgoat hits Take Profit, it takes your Wager.
        </div>
        {serverOpenPositions?.length === 0 ? (
          <div className="text-center text-white/30 text-xs py-4 font-mono border border-white/5 border-dashed rounded-lg bg-black/20">
            ALgoat currently has no open positions.
          </div>
        ) : (
          <div className="flex flex-col gap-2 max-h-[400px] overflow-y-auto pr-1 custom-scrollbar">
            {[...(serverOpenPositions || [])].sort((a, b) => (b.pnlPct || 0) - (a.pnlPct || 0)).map(p => {
              const meta = p.tokenMeta || {};
              const pnlPct = p.pnlPct || 0;
              const isProfit = pnlPct > 0;
              return (
                <div key={p.mint} className="flex items-center justify-between bg-white/5 hover:bg-white/10 transition-colors border border-white/10 rounded-lg p-2.5">
                  <div className="flex items-center gap-3 min-w-[120px]">
                    {meta.imageUri ? (
                      <>
                        <img 
                          src={meta.imageUri} 
                          className="size-8 rounded-full border border-white/10 bg-black/50 object-cover shrink-0" 
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const next = e.currentTarget.nextElementSibling as HTMLElement;
                            if (next) next.style.display = 'flex';
                          }}
                        />
                        <div className="size-8 rounded-full bg-black/40 border border-white/10 items-center justify-center text-white/30 text-[10px] shrink-0" style={{ display: 'none' }}>?</div>
                      </>
                    ) : (
                      <div className="size-8 rounded-full bg-black/40 border border-white/10 flex items-center justify-center text-white/30 text-[10px] shrink-0">?</div>
                    )}
                    <div className="flex flex-col">
                      <span className="text-white font-bold text-xs truncate max-w-[80px]">{meta.symbol || truncate(p.mint, 4)}</span>
                      <span className={`text-[10px] font-mono font-black ${isProfit ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {isProfit ? '+' : ''}{(pnlPct * 100).toFixed(2)}%
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex flex-col flex-1 items-center px-2">
                     <span className="text-white/30 text-[9px] font-mono">Bot Entry</span>
                     <span className="text-white/80 text-[10px] font-mono">{formatUsdCompact(p.entryMcUsd)}</span>
                  </div>

                  <button
                    onClick={() => handleOpenPosition("down", p.mint)}
                    disabled={paperBalance < wager}
                    className="flex items-center gap-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 px-3 py-1.5 rounded font-black uppercase tracking-widest text-[9px] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    title={`Short with ${leverage}x leverage for ${wager} SOL`}
                  >
                    <TrendingDown className="size-3" /> Short
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex-1">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] font-black uppercase tracking-widest text-white/50 flex items-center gap-2">
            <Clock className="size-3" /> Active Positions
          </div>
          {positions.some(p => p.status === "open") && (
            <button 
              onClick={handleCloseAll}
              className="text-[9px] font-black uppercase tracking-widest bg-red-500/10 hover:bg-red-500/20 text-red-400 px-2 py-1 rounded transition-colors"
            >
              Close All
            </button>
          )}
        </div>
        
        {positions.filter(p => p.status === "open").length === 0 ? (
          <div className="text-center text-white/30 text-xs py-8 border border-white/5 border-dashed rounded-xl bg-white/[0.02]">
            No open positions
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {positions.filter(p => p.status === "open").map(p => {
              const isLegacy = typeof p.entryMcUsd !== "number";
              let rawPnl = 0;
              let pnlPct = 0;
              let currentMcUsd = 0;
              
              if (isLegacy) {
                const svixDiff = p.side === "up" 
                  ? (currentSvixValue - (p.entrySvix || 20))
                  : ((p.entrySvix || 20) - currentSvixValue);
                pnlPct = svixDiff / 100;
                rawPnl = p.wagerSol * (p.leverage || 1) * pnlPct;
              } else {
                currentMcUsd = livePrices[p.mint] || p.entryMcUsd;
                pnlPct = p.side === "up" 
                  ? ((currentMcUsd - p.entryMcUsd) / p.entryMcUsd)
                  : ((p.entryMcUsd - currentMcUsd) / p.entryMcUsd);
                rawPnl = p.wagerSol * (p.leverage || 1) * pnlPct;
              }
              
              const isProfit = rawPnl >= 0;
              
              const borderColor = pnlPct >= 0.1 ? "#10b981"
                : pnlPct >= 0.02 ? "#34d399"
                : pnlPct >= 0 ? "#6ee7b7"
                : pnlPct >= -0.05 ? "#fb7185"
                : "#f43f5e";

              return (
                <div key={p.id} className="relative border border-white/10 bg-white/5 rounded-xl group/row overflow-hidden flex flex-col mb-1">
                  {/* Left PnL color strip */}
                  <div
                    className="absolute left-0 top-0 bottom-0 w-[4px] transition-colors duration-700 z-20"
                    style={{ background: borderColor }}
                  />

                  {/* Manual Close Button (Absolute top-right corner) */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClosePosition(p.id);
                    }}
                    className="absolute top-2 right-2 z-30 opacity-0 group-hover/row:opacity-100 transition-opacity p-1.5 hover:bg-rose-500/20 bg-black/80 border border-transparent hover:border-rose-500/30 rounded text-white/40 hover:text-rose-400 shadow-md"
                    title="Manually Close Trade"
                  >
                    <X className="size-3" />
                  </button>

                  {/* Row content */}
                  <div className="relative z-10 p-2.5 pl-4 hover:bg-white/[0.02] transition-colors flex flex-wrap items-center justify-between gap-x-4 gap-y-3 text-[10px] font-mono">
                    
                    {/* Left: Token Info */}
                    <div className="flex items-center gap-3 min-w-[160px] shrink-0">
                      {p.tokenImageUri ? (
                        <img
                          src={p.tokenImageUri}
                          alt={p.tokenSymbol}
                          className="size-10 rounded-full border border-white/10 shrink-0 object-cover bg-black/50"
                        />
                      ) : (
                        <div className="size-10 rounded-full bg-black/40 border border-white/10 flex items-center justify-center text-white/30 shrink-0 text-xs">
                          {p.tokenSymbol?.[0] || "?"}
                        </div>
                      )}

                      <div className="flex flex-col min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-bold text-white text-[12px] truncate max-w-[100px] text-left">
                            {p.tokenSymbol || "LEGACY"}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <div className="flex items-center gap-1 text-white/40 group-hover:text-white transition-colors bg-black/20 px-1 py-0.5 rounded text-[9px]">
                            <span>{truncate(p.mint, 4) || "SVIX Position"}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Middle: Key Metrics */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 flex-1 min-w-[250px]">
                      <div className="flex items-center gap-1.5 bg-black/30 px-2 py-1 rounded">
                        <span className="text-white/30">MC:</span>
                        <span className="text-white/80">{isLegacy ? p.entrySvix?.toFixed(1) : formatUsdCompact(p.entryMcUsd)}</span>
                        {!isLegacy && (
                          <>
                            <span className="text-white/30">→</span>
                            <span className="text-white/80 animate-pulse">{formatUsdCompact(currentMcUsd)}</span>
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 bg-black/30 px-2 py-1 rounded">
                        <span className="text-white/30">Wager:</span>
                        <span className="text-white/80">{p.wagerSol} SOL</span>
                      </div>
                      <div className="flex items-center gap-1.5 bg-black/30 px-2 py-1 rounded">
                        <span className="text-white/30">Time:</span>
                        <span className="text-white/80 flex items-center gap-1">
                          <Clock className="size-2.5 text-blue-400" />
                          {formatDuration(Date.now() - p.entryTs)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className={`font-black uppercase tracking-widest text-[9px] px-1.5 py-1 rounded ${p.side === "up" ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : "bg-red-500/10 text-red-400 border border-red-500/20"}`}>
                          {p.leverage || 1}x {p.side === "up" ? "LONG" : "SHORT"}
                        </span>
                      </div>
                    </div>

                    {/* Right: PnL & Actions */}
                    <div className="relative flex items-center shrink-0 ml-auto pr-6">
                      <div className="flex flex-col items-end gap-1">
                        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full font-black text-[12px] border ${isProfit ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border-rose-500/20'}`}>
                          {isProfit ? <TrendingUp className="size-3.5" /> : <TrendingDown className="size-3.5" />}
                          {isProfit ? "+" : ""}{(pnlPct * (p.leverage || 1) * 100).toFixed(2)}%
                        </div>
                        <span className="text-white/50 text-[9px] uppercase tracking-wide mr-1 font-bold">
                          {isProfit ? "+" : ""}{rawPnl.toFixed(4)} SOL
                        </span>
                      </div>
                    </div>

                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {positions.filter(p => p.status === "closed").length > 0 && (
          <div className="mt-8">
            <div className="text-[10px] font-black uppercase tracking-widest text-white/50 mb-3 flex items-center gap-2">
              <CheckCircle2 className="size-3" /> History
            </div>
            <div className="flex flex-col gap-2 opacity-60">
              {positions.filter(p => p.status === "closed").slice(0, 5).map(p => {
                const isProfit = (p.pnlSol || 0) >= 0;
                const isLegacy = typeof p.entryMcUsd !== "number";
                return (
                  <div key={p.id} className="flex flex-col gap-2 text-xs bg-black/30 p-2.5 rounded-lg border border-white/5">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <span className={`font-black uppercase tracking-widest text-[9px] px-1 py-0.5 rounded ${p.side === "up" ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"}`}>
                          {p.leverage || 1}x {p.side === "up" ? "LONG" : "SHORT"}
                        </span>
                        <span className="font-bold">{p.tokenSymbol || "LEGACY"}</span>
                      </div>
                      <div className={`font-mono font-bold ${isProfit ? "text-emerald-400" : "text-red-400"}`}>
                        {isProfit ? "+" : ""}{p.pnlSol?.toFixed(4)} SOL
                      </div>
                    </div>
                    <div className="flex justify-between text-[10px] font-mono text-white/40">
                      <span>{p.wagerSol} SOL</span>
                      <span>
                        {isLegacy 
                          ? `${p.entrySvix?.toFixed(1)} → ${p.exitSvix?.toFixed(1)}` 
                          : `${formatUsdCompact(p.entryMcUsd)} → ${formatUsdCompact(p.exitMcUsd || 0)}`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
